/**
 * Converts a UK postcode to latitude and longitude coordinates
 * @param {string} postcode - The UK postcode to geocode
 * @returns {Promise<{latitude: number, longitude: number}|null>} Coordinates or null if not found
 */
export const geocodePostcode = async (postcode) => {
  try {
    // Clean up postcode format
    const cleanPostcode = postcode.replace(/\s+/g, ' ').trim();
    
    // Using Nominatim API (free OpenStreetMap geocoding service)
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?format=json&country=GB&postalcode=${encodeURIComponent(cleanPostcode)}&limit=1`,
      {
        headers: {
          'User-Agent': 'CoolyDentalApp/1.0 (support@coooly.co.uk)', // Identifies our application to the API provider
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`Geocoding API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data && data.length > 0) {
      return {
        latitude: parseFloat(data[0].lat),
        longitude: parseFloat(data[0].lon)
      };
    }
    
    console.warn('No geocoding results for postcode:', cleanPostcode);
    return null;
  } catch (error) {
    console.error('Geocoding error:', error);
    return null; // Return null instead of throwing to allow job creation to continue
  }
};

/**
 * Converts a UK city/location name to latitude and longitude coordinates
 * @param {string} cityName - The UK city or location name to geocode
 * @returns {Promise<{latitude: number, longitude: number}|null>} Coordinates or null if not found
 */
export const geocodeCity = async (cityName) => {
  try {
    // Clean up city name
    const cleanCityName = cityName.trim();
    
    // Using Nominatim API specifically for city search
    const response = await fetch(
      `https://nominatim.openstreetmap.org/search?city=${encodeURIComponent(cleanCityName)}&format=json&country=GB&limit=1`,
      {
        headers: {
          'User-Agent': 'CooolyDentalApp/1.0 (support@coooly.co.uk)',
        }
      }
    );
    
    if (!response.ok) {
      throw new Error(`City geocoding API error: ${response.status}`);
    }
    
    const data = await response.json();
    
    if (data && data.length > 0) {
      console.log(`Successfully geocoded city "${cleanCityName}":`, {
        latitude: parseFloat(data[0].lat),
        longitude: parseFloat(data[0].lon),
        displayName: data[0].display_name
      });
      
      return {
        latitude: parseFloat(data[0].lat),
        longitude: parseFloat(data[0].lon)
      };
    }
    
    console.warn('No geocoding results for city:', cleanCityName);
    return null;
  } catch (error) {
    console.error('City geocoding error:', error);
    return null;
  }
};

/**
 * Smart geocoding function that attempts both postcode and city geocoding
 * @param {string} location - Location string (could be postcode or city)
 * @returns {Promise<{latitude: number, longitude: number, method: string}|null>} Coordinates with method used, or null
 */
export const geocodeLocation = async (location) => {
  try {
    const cleanLocation = location.trim();
    
    // First try postcode geocoding (for full UK postcodes)
    const postcodeRegex = /^[A-Z]{1,2}[0-9][A-Z0-9]?\s?[0-9][A-Z]{2}$/i;
    if (postcodeRegex.test(cleanLocation)) {
      console.log(`Attempting postcode geocoding for: ${cleanLocation}`);
      const postcodeResult = await geocodePostcode(cleanLocation);
      if (postcodeResult) {
        return {
          ...postcodeResult,
          method: 'postcode'
        };
      }
    }
    
    // If postcode fails or doesn't look like a postcode, try city geocoding
    console.log(`Attempting city geocoding for: ${cleanLocation}`);
    const cityResult = await geocodeCity(cleanLocation);
    if (cityResult) {
      return {
        ...cityResult,
        method: 'city'
      };
    }
    
    console.warn('No geocoding results found for location:', cleanLocation);
    return null;
  } catch (error) {
    console.error('Smart geocoding error:', error);
    return null;
  }
};

/**
 * Calculates the distance between two points using the Haversine formula
 * @param {number} lat1 - First point latitude
 * @param {number} lon1 - First point longitude
 * @param {number} lat2 - Second point latitude
 * @param {number} lon2 - Second point longitude
 * @returns {number} Distance in miles
 */
export const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 3959; // Earth's radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = 
    Math.sin(dLat/2) * Math.sin(dLat/2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
    Math.sin(dLon/2) * Math.sin(dLon/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c; // Distance in miles
};
